import type { Modifier } from '@dnd-kit/core';

/**
 * Locks drag movement to the vertical axis (X = 0). Used for vertical lists
 * so users can't drag rows sideways out of the container.
 */
export const restrictToVerticalAxis: Modifier = ({ transform }) => ({
    ...transform,
    x: 0,
});

/**
 * Locks drag movement to the horizontal axis (Y = 0).
 */
export const restrictToHorizontalAxis: Modifier = ({ transform }) => ({
    ...transform,
    y: 0,
});

/**
 * Clamps the dragged element so it can't travel past the edges of its
 * scroll/overflow container. Without this, a user can drag an item
 * arbitrarily far from the list and cause the parent to scroll / overflow
 * indefinitely.
 *
 * This is a local copy of `@dnd-kit/modifiers`' `restrictToParentElement`
 * so we don't need to pull in another package just for two helpers.
 */
export const restrictToParentElement: Modifier = ({ containerNodeRect, draggingNodeRect, transform }) => {
    if (!draggingNodeRect || !containerNodeRect) return transform;

    const value = { ...transform };

    if (draggingNodeRect.top + transform.y <= containerNodeRect.top) {
        value.y = containerNodeRect.top - draggingNodeRect.top;
    } else if (draggingNodeRect.bottom + transform.y >= containerNodeRect.top + containerNodeRect.height) {
        value.y = containerNodeRect.top + containerNodeRect.height - draggingNodeRect.bottom;
    }

    if (draggingNodeRect.left + transform.x <= containerNodeRect.left) {
        value.x = containerNodeRect.left - draggingNodeRect.left;
    } else if (draggingNodeRect.right + transform.x >= containerNodeRect.left + containerNodeRect.width) {
        value.x = containerNodeRect.left + containerNodeRect.width - draggingNodeRect.right;
    }

    return value;
};
